import React, { Fragment, useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../../overmind';
import { EmptyState } from '../../empty-state/empty-state';
import { ScoreType } from '../../../../enums';
import styles from '../average.module.css';
import { ExceedsExpectationsIcon } from './exceeds-expectations.icon';
import { MeetsExpectationsIcon } from './meets-expectations.icon';
import { BelowExpectationsIcon } from './below-expectations.icon';
import { IRoleAverageWidgetProps } from '../average.models';
import { Average } from '../average';
import { EntityScore } from '../../../../models/overmind/analytics';
import { WidgetCard } from '../../../../pages/analytics/analytics-filters/widget-card';
import { SkeletonOverlay } from '../../loading-state/skeleton-overlay';

export function RoleAverageWidget({ personName, baseSearchParams }: IRoleAverageWidgetProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [roleScore, setRoleScore] = useState<EntityScore>();

  useEffect(() => {
    async function getData() {
      setIsLoading(true);

      const response = await actions.analyticsGetScoresByScoreType({
        ...baseSearchParams,
        pageSize: 1,
        scoreType: ScoreType.Role,
      });
      setRoleScore(response?.results[0]);

      setIsLoading(false);
    }

    getData();
  }, [companyVariables.slug, baseSearchParams.roleSlug, baseSearchParams.learnerSlug]);

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%">
      <WidgetCard title="">
        {!roleScore ? (
          <EmptyState title="No averages found" />
        ) : (
          <Fragment>
            <Average title={personName ?? 'Role average'} description={!!personName ? `Average of ${personName} in this role` : 'The role average is the average of all the people in this role'} score={roleScore.score} />

            <div className={styles.divider} />

            <Average title="Expected proficiency" score={roleScore.comparisonScore} scoreIndicatorTitle="Average skill expectation" noScoreText="Not captured" />

            {!!roleScore.score && !!roleScore.comparisonScore && (
              <Fragment>
                <div className={styles.divider} />
                <div className={styles.container} style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                  {companyVariables.useLevels ? (
                    <Fragment>
                      {roleScore.score.level > roleScore.comparisonScore.level && (
                        <Fragment>
                          <ExceedsExpectationsIcon />
                          <h5>Exceeding expectation</h5>
                        </Fragment>
                      )}

                      {roleScore.score.level === roleScore.comparisonScore.level && (
                        <Fragment>
                          <MeetsExpectationsIcon />
                          <h5>Meeting expectation</h5>
                        </Fragment>
                      )}

                      {roleScore.score.level < roleScore.comparisonScore.level && (
                        <Fragment>
                          <BelowExpectationsIcon />
                          <h5>Below expectation</h5>
                        </Fragment>
                      )}
                    </Fragment>
                  ) : (
                    <Fragment>
                      {roleScore.score.percentage > roleScore.comparisonScore.percentage && (
                        <Fragment>
                          <ExceedsExpectationsIcon />
                          <h5>Exceeding expectation</h5>
                        </Fragment>
                      )}

                      {roleScore.score.percentage === roleScore.comparisonScore.percentage && (
                        <Fragment>
                          <MeetsExpectationsIcon />
                          <h5>Meeting expectation</h5>
                        </Fragment>
                      )}

                      {roleScore.score.percentage < roleScore.comparisonScore.percentage && (
                        <Fragment>
                          <BelowExpectationsIcon />
                          <h5>Below expectation</h5>
                        </Fragment>
                      )}
                    </Fragment>
                  )}
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </WidgetCard>
    </SkeletonOverlay>
  );
}
